import React, { forwardRef } from "react"

/**
 * This HOC is intended to be used as part of the migration from MUI to Mantine.
 * It maps the props from one component to another.
 * It always return a new component using React.forwardRef in case the original component is using ref.
 * @param Component - The component to which the props are to be mapped.
 * @param mapperFn - The function that maps the props from one component to another.
 * @param handleRef - A boolean that indicates if the ref should be handled inside the Mapper function. In some cases the ref in the MUI component refers to the root element, while in Mantine it refers to the input element.
 * @returns The component with the mapped props.
 */
export const withMappedProps = <P extends object, Q extends object>(
  Component: React.ComponentType<P>,
  mapperFn: (input: Q, handleRef?: boolean, ref?: any) => P,
  handleRef = false,
) => {
  const WrappedComponent = forwardRef<any, Q>((props, ref) => {
    const mappedProps = mapperFn(props as Q, handleRef, ref)
    const newProps = {
      ...mappedProps,
      ...(handleRef ? { ref } : {}),
    }
    return <Component {...newProps} />
  })

  // It inherits the display name of the original component
  WrappedComponent.displayName = `withMappedProps(${
    Component.displayName || Component.name
  })`

  return WrappedComponent
}

export default withMappedProps
